import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  type SupportedLanguage,
  type TranslationKey,
  translations,
  formatTranslation,
} from '../utils/translations';

export interface LanguageContextType {
  language: SupportedLanguage;
  direction: 'ltr' | 'rtl';
  isRTL: boolean;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const STORAGE_KEY = 'priceloss_language';

const defaultLanguageContext: LanguageContextType = {
  language: 'en',
  direction: 'ltr',
  isRTL: false,
  setLanguage: () => {},
  t: (key: TranslationKey, params?: Record<string, string | number>): string => {
    const raw = (translations.en as Record<string, string>)[key] ?? key;
    return formatTranslation(raw, params);
  },
};

const LanguageContext = createContext<LanguageContextType>(defaultLanguageContext);

function getInitialLanguage(): SupportedLanguage {
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem(STORAGE_KEY) as SupportedLanguage | null;
    if (stored && stored in translations) {
      return stored;
    }
    if (navigator.language) {
      const prefix = navigator.language.slice(0, 2).toLowerCase();
      if (prefix in translations) {
        return prefix as SupportedLanguage;
      }
    }
  }
  return 'en';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);

  // Cross-tab synchronization
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && e.newValue in translations) {
        setLanguageState(e.newValue as SupportedLanguage);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, lang);
    }
  };

  const direction = language === 'ar' ? 'rtl' : 'ltr';
  const isRTL = direction === 'rtl';

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', language);
    }
  }, [language, direction]);

  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    const langDict = translations[language] ?? translations.en;
    const raw = (langDict as Record<string, string>)[key] ?? (translations.en as Record<string, string>)[key] ?? key;
    return formatTranslation(raw, params);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        direction,
        isRTL,
        setLanguage,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  return context || defaultLanguageContext;
}
